/**
 * Sync Manager — orchestrates bootstrap + the three pull triggers.
 *
 *   0. Bootstrap (one-shot)  → on first app load per device, run /sync/bootstrap
 *                              to populate Dexie with the full active dataset.
 *                              Pull is a no-op until bootstrap completes.
 *   1. App start             → after bootstrap, run one pull cycle (if online).
 *   2. Periodic              → setInterval every PULL_INTERVAL_MS while online.
 *   3. Network reconnect     → window "online" event runs one cycle (and
 *                              retries bootstrap if it never succeeded).
 *
 * The manager is a singleton: `start()` is idempotent so a strict-mode double
 * mount in React doesn't spin up two intervals or two listeners.
 */

import { PULL_INTERVAL_MS } from "../constants";
import { getDek } from "../db/crypto";
import { logger } from "../utils/logger";
import { isBootstrapComplete, runBootstrap } from "./bootstrap-engine";
import { runPullCycle } from "./pull-engine";

let started = false;
let intervalId: number | null = null;
let onlineHandler: (() => void) | null = null;
let offlineHandler: (() => void) | null = null;

export function start(): void {
  if (started) return;
  started = true;

  // Trigger 1 — fire once on start, even before the interval is armed, so the
  // doctor sees fresh data on app open without waiting up to 5 minutes.
  void kick("startup");

  // Trigger 2 — periodic. Skipped internally if offline.
  intervalId = window.setInterval(() => { void kick("interval"); }, PULL_INTERVAL_MS);

  // Trigger 3 — coming back online.
  onlineHandler  = () => { void kick("online"); };
  // The "offline" event is wired only so the engine can emit an offline state
  // via its next cycle — runPullCycle short-circuits when navigator.onLine is
  // false. No extra work needed here.
  offlineHandler = () => { /* state transition handled inside pull engine */ };

  window.addEventListener("online",  onlineHandler);
  window.addEventListener("offline", offlineHandler);
}

export function stop(): void {
  if (!started) return;
  started = false;

  if (intervalId !== null) {
    window.clearInterval(intervalId);
    intervalId = null;
  }
  if (onlineHandler)  window.removeEventListener("online",  onlineHandler);
  if (offlineHandler) window.removeEventListener("offline", offlineHandler);
  onlineHandler = null;
  offlineHandler = null;
}

async function kick(trigger: "startup" | "interval" | "online"): Promise<void> {
  // Warm the DEK cache every time we're online so crypto_keys is populated
  // even in sessions where the pull cycle has zero records to encrypt.
  // Non-fatal: if DEK fetch fails (offline, KMS down), sync continues —
  // wrapForStorage will surface the error per-record instead.
  void getDek().catch(() => {});

  // Bootstrap is a no-op once it has completed once on this device, so we can
  // safely call it on every trigger. The first ever call performs the full
  // initial sync; every subsequent call returns `already_done` immediately.
  // Wiring it into every trigger (not just startup) means a user who first
  // loaded the app while offline will bootstrap on the next "online" event.
  if (!isBootstrapComplete()) {
    const bs = await runBootstrap();
    if (!bs.ok) {
      logger.warn(`[sync-manager] ${trigger} bootstrap skipped/failed`, { reason: bs.reason ?? "" });
      return; // Pull would no-op anyway (bootstrap flag absent) — return early.
    }
  }

  // runPullCycle handles its own offline / error state and will never throw,
  // so we only need to log the trigger context for telemetry.
  const result = await runPullCycle();
  if (!result.ok && result.reason !== "offline") {
    logger.warn(`[sync-manager] ${trigger} pull skipped/failed`, { reason: result.reason ?? "" });
  }
}
